import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  Typography,
  FormControl,
  FormLabel,
  Textarea,
  Input,
  Select,
  Option,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
} from '@mui/joy';
import { FloppyDisk as Save } from '@phosphor-icons/react';
import { useUpdateContactNote } from '../../../hooks/useCrm';
import type { ContactNote, NoteCategory, NotePriority } from '../../../types/crm';

interface EditNoteModalProps {
  open: boolean;
  onClose: () => void;
  note: ContactNote | null;
}

interface EditNoteFormData {
  subject: string;
  content: string;
  category: NoteCategory;
  priority: NotePriority;
  is_private: boolean;
  requires_followup: boolean;
  followup_date?: string;
  followup_completed: boolean;
}

const noteCategories: { value: NoteCategory; label: string }[] = [
  { value: 'general', label: 'General Note' },
  { value: 'call', label: 'Phone Call' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'email', label: 'Email' },
  { value: 'followup', label: 'Follow-up' },
];

const notePriorities: { value: NotePriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export const EditNoteModal: React.FC<EditNoteModalProps> = ({
  open,
  onClose,
  note,
}) => {
  const [formData, setFormData] = useState<EditNoteFormData>({
    subject: '',
    content: '',
    category: 'general',
    priority: 'medium',
    is_private: false,
    requires_followup: false,
    followup_date: '',
    followup_completed: false,
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const updateNoteMutation = useUpdateContactNote();

  // Initialize form data when note changes
  useEffect(() => {
    if (note) {
      setFormData({
        subject: note.subject,
        content: note.note,
        category: note.category,
        priority: note.priority,
        is_private: note.is_private,
        requires_followup: note.requires_followup,
        followup_date: note.followup_date ? note.followup_date.slice(0, 16) : '', // Format for datetime-local input
        followup_completed: note.followup_completed,
      });
    }
  }, [note]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!note) return;
    
    // Reset errors
    setErrors({});
    
    // Validation
    const newErrors: Record<string, string> = {};
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Note content is required';
    }
    
    if (formData.requires_followup && !formData.followup_date) {
      newErrors.followup_date = 'Follow-up date is required when follow-up is needed';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const updateData = {
        id: note.id,
        subject: formData.subject,
        note: formData.content,
        category: formData.category,
        priority: formData.priority,
        is_private: formData.is_private,
        requires_followup: formData.requires_followup,
        followup_date: formData.requires_followup && formData.followup_date ? formData.followup_date : undefined,
        followup_completed: formData.followup_completed,
      };

      await updateNoteMutation.mutateAsync(updateData);
      onClose();
    } catch {
      // Intentionally empty
    }
  };

  const handleInputChange = (field: keyof EditNoteFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  if (!note) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: '90vw', maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }}>
        <ModalClose />
        <Typography level="h3" sx={{ mb: 2 }}>
          Edit Note
        </Typography>

        {updateNoteMutation.isError && (
          <Alert color="danger" sx={{ mb: 2 }}>
            Failed to update note. Please try again.
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {/* Subject */}
            <FormControl error={!!errors.subject}>
              <FormLabel>Subject *</FormLabel>
              <Input
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                placeholder="Brief description of the note"
                disabled={updateNoteMutation.isPending}
              />
              {errors.subject && (
                <Typography level="body-xs" color="danger">
                  {errors.subject}
                </Typography>
              )}
            </FormControl>

            {/* Content */}
            <FormControl error={!!errors.content}>
              <FormLabel>Note Content *</FormLabel>
              <Textarea
                value={formData.content}
                onChange={(e) => handleInputChange('content', e.target.value)}
                placeholder="Enter your note here..."
                minRows={4}
                maxRows={8}
                disabled={updateNoteMutation.isPending}
              />
              {errors.content && (
                <Typography level="body-xs" color="danger">
                  {errors.content}
                </Typography>
              )}
            </FormControl>

            {/* Category and Priority */}
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Category</FormLabel>
                <Select
                  value={formData.category}
                  onChange={(_, value) => handleInputChange('category', value)}
                  disabled={updateNoteMutation.isPending}
                >
                  {noteCategories.map((category) => (
                    <Option key={category.value} value={category.value}>
                      {category.label}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Priority</FormLabel>
                <Select
                  value={formData.priority}
                  onChange={(_, value) => handleInputChange('priority', value)}
                  disabled={updateNoteMutation.isPending}
                >
                  {notePriorities.map((priority) => (
                    <Option key={priority.value} value={priority.value}>
                      {priority.label}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Privacy */}
            <FormControl>
              <Checkbox
                checked={formData.is_private}
                onChange={(e) => handleInputChange('is_private', e.target.checked)}
                label="Private note (only visible to you)"
                disabled={updateNoteMutation.isPending}
              />
            </FormControl>

            {/* Follow-up */}
            <FormControl>
              <Checkbox
                checked={formData.requires_followup}
                onChange={(e) => handleInputChange('requires_followup', e.target.checked)}
                label="Requires follow-up"
                disabled={updateNoteMutation.isPending}
              />
            </FormControl>

            {/* Follow-up Date */}
            {formData.requires_followup && (
              <FormControl error={!!errors.followup_date}>
                <FormLabel>Follow-up Date *</FormLabel>
                <Input
                  type="datetime-local"
                  value={formData.followup_date}
                  onChange={(e) => handleInputChange('followup_date', e.target.value)}
                  disabled={updateNoteMutation.isPending}
                />
                {errors.followup_date && (
                  <Typography level="body-xs" color="danger">
                    {errors.followup_date}
                  </Typography>
                )}
              </FormControl>
            )}

            {/* Follow-up completed (only show if follow-up is required) */}
            {formData.requires_followup && (
              <FormControl>
                <Checkbox
                  checked={formData.followup_completed}
                  onChange={(e) => handleInputChange('followup_completed', e.target.checked)}
                  label="Follow-up completed"
                  disabled={updateNoteMutation.isPending}
                />
              </FormControl>
            )}

            {/* Actions */}
            <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={updateNoteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="solid"
                startDecorator={updateNoteMutation.isPending ? <CircularProgress size="sm" /> : <Save />}
                disabled={updateNoteMutation.isPending}
              >
                {updateNoteMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
};